import * as React from 'react';
import { useSelector } from 'react-redux';

import Space from 'antd/lib/space';

import Typography from 'antd/lib/typography';

import { Nullable } from '@common/typescript/objects/Nullable';

import { Translator } from '@app/internationalization/init';
import { Location, locationName } from '@app/objects/Location';
import { ViewMode } from '@app/components/Utils/Utils';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';

import {
	ServiceType,
	DeliveryType,
	PetOnHold, PetEngraving, formatServiceTypeName,
} from '@app/objects/Pet';
import { Clinic, ClinicListInfo } from '@app/objects/Clinic';
import { AddressDetails } from '@app/services/AddressService';
import { ApplicationState } from '@app/store';
import { CrematoryListInfo } from '@app/objects/Crematory';
import { DateView } from '@app/components/UI/Views/DateView/DateView';
import { DeliveryTypeView } from '@app/components/UI/Views/DeliveryTypeView';

import { formatAddress } from '@app/utilities/address';
import { PetPrice } from '@app/objects/PetPrice';
import { PriceKind } from '@app/objects/Price';
import { DeliveryManager } from '@app/hooks/Delivery/useDeliveryManager';

export interface BaseSectionProps {
	mode: ViewMode;
	crematoryId: number;
	crematory?: CrematoryListInfo;
}

export function getPetInfoFields(pet: FormValues, t: Translator, showInternalId: boolean) {
	const setTagId = { title: 'Tag Id', data: pet.internalIdNum };
	const engraving = (pet.engraving ?? [])
		.filter((q: PetEngraving) => Boolean(q.name))
		.sort((a: PetEngraving, b: PetEngraving) => a.order - b.order)
		.map((q: PetEngraving) => (<li key={q.id}>{q.name}</li>));
	const setEngraving = { title: t('engraving'), data: <ul className="">{engraving}</ul> };

	return [
		{ title: t('name'), data: pet.name },
		{ title: t('gender'), data: pet.gender },
		{ title: t('weight'), data: pet.reportedWeight },
		{ title: t('species'), data: pet.petSpecie },
		{ title: t('breed'), data: pet.petBreed },
		{ title: t('breed-type'), data: pet.isMixed ? 'Mixed' : 'Pure' },
		{ title: t('color'), data: pet.color },
		{ title: t('date-of-birth'), data: <DateView date={pet.dateOfBirth} /> },
		showInternalId ? setTagId : undefined,
		pet.serviceType === ServiceType.Communal ? undefined : setEngraving,
	];
}

interface OwnerInfoConfig {
	pet: FormValues,
	t: Translator
	clinic?: Nullable<Clinic>;
}

function hasLocationInfo(pet: FormValues): boolean {
	if (pet.ownerStateId) return true;
	if (pet.ownerCity) return true;
	if (pet.ownerState) return true;
	if (pet.ownerAddress) return true;
	if (pet.ownerAddress2) return true;

	return Boolean(pet.ownerZip);
}

export function getOwnerInfoFields({ pet, t, clinic }: OwnerInfoConfig) {
	const ownerLocation = {
		title: t('owner-location'),
		data: locationName({
			id: 0,
			name: '',
			stateId: pet.ownerStateId,
			city: pet.ownerCity,
			state: pet.ownerState,
			streetAddress: pet.ownerAddress,
			streetAddress2: pet.ownerAddress2,
			zip: pet.ownerZip,
		}),
	};

	return [
		{ title: t('owner-first-name'), data: pet.ownerFirstName },
		{ title: t('owner-last-name'), data: pet.ownerLastName },
		pet.discount ? { title: t('discount'), data: pet.discount } : undefined,
		clinic && clinic?.showOwnerAddress && hasLocationInfo(pet) ? ownerLocation : undefined,
	];
}

interface CremationInfoConfig {
	pet: FormValues;
	t: Translator;
	clinic: Nullable<Clinic>;
	locations: Array<Location>;
	clinics?: Array<ClinicListInfo>;
	services: Map<ServiceType, string>;
	deliveryManager: DeliveryManager;
}

function getPreset(services: Array<PetPrice>): Nullable<PetPrice> {
	return services.find((item: PetPrice) => item.price?.priceKind === PriceKind.Preset) ?? null;
}

const deliveryKeyPrefix = 'editors.pet-editor.labels';

function getTrackingNumber(pet: FormValues, deliveryManager: DeliveryManager, t: Translator) {
	const deliveryValue = deliveryManager.type === 'modern'
		? pet.deliveryTypeId ?? null
		: pet.deliveryType ?? null;

	if (!deliveryManager.isMail(deliveryValue)) return undefined;

	return { title: t(`${deliveryKeyPrefix}.tracking-number`), data: pet.trackingNumber ?? '—' };
}

const keyPrefix = 'editors.clinic-pet-editor.cards.cremation-info';
export function getCremationInfo({
	clinic,
	t,
	pet,
	locations,
	clinics,
	services,
	deliveryManager,
}: CremationInfoConfig) {
	const preset = getPreset(pet.services);
	const onHold = { title: 'On hold', data: pet.onHold === PetOnHold.ByClinic ? 'Pet is on hold' : 'Pet is not on hold' };
	const onRush = { title: 'Rush', data: pet.rush ? 'Pet processing is urgent' : 'Pet processing is normal' };
	const setDelivery = {
		title: 'Delivery Type',
		data: (
			<DeliveryTypeView
				value={{ deliveryType: pet.deliveryType ?? DeliveryType.None, deliveryTypeId: pet.deliveryTypeId }}
			/>
		),
	};
	const location = locations.find((item: Location) => item.id === pet.clinicLocationId);
	const deliveryLocation = locations.find((loc) => loc.id === pet.deliveryLocationId);
	const deliveryClinic = clinics?.find((item: ClinicListInfo) => item.id === pet.deliveryClinicId);
	const serviceType = pet.serviceType === undefined ? 'Unknown' : services.get(pet.serviceType) ?? 'Unknown';
	const trackingNumber = getTrackingNumber(pet, deliveryManager, t);

	return [
		{ title: t(`${keyPrefix}.cremation-type`), data: formatServiceTypeName(serviceType, pet.isSpecial, pet.rush) },
		{ title: t(`${keyPrefix}.clinic-location`), data: location ? locationName(location) : 'None' },
		clinic?.canSetDeliveryType ? setDelivery : undefined,
		pet.deliveryClinicId ? { title: t(`${keyPrefix}.delivery-clinic`), data: deliveryClinic?.name } : undefined,
		pet.deliveryLocationId
			? {
				title: t(`${keyPrefix}.delivery-location`),
				data:
				<Space direction="vertical" size={0}>
					<Typography.Text>{deliveryLocation.name ?? '—'}</Typography.Text>
					<Typography.Text style={{ opacity: 0.6, fontSize: '.9em', lineHeight: 1.1 }}>
						{formatAddress(deliveryLocation) || '-'}
					</Typography.Text>
				</Space>,
			} : undefined,
		preset ? { title: t(`${keyPrefix}.preset`), data: preset.name } : undefined,
		trackingNumber,
		clinic?.canPutOnHold ? onHold : undefined,
		clinic?.canPutOnRush ? onRush : undefined,
	];
}

export function getDeliveryInfoFields(pet: FormValues, t: Translator, details: AddressDetails) {
	const states = useSelector((state: ApplicationState) => state.selects.regions.items);
	const state = pet.deliveryState ?? states.find((i) => i.id === pet.deliveryStateId);

	return [
		{ title: t(`${deliveryKeyPrefix}.delivery-address-1`), data: pet.deliveryAddress },
		{ title: t(`${deliveryKeyPrefix}.delivery-address-2`), data: pet.deliveryAddress2 },
		{ title: t(`${deliveryKeyPrefix}.delivery-city`), data: pet.deliveryCity },
		{ title: t(`${deliveryKeyPrefix}.delivery-${details.mailKey}`), data: pet.deliveryZip },
		{ title: t(`${deliveryKeyPrefix}.delivery-${details.regionKey}`), data: state?.name },
	];
}
